import { Markup as InterweaveMarkup } from 'interweave';
import React, { useState } from 'react';

import { ExternalLink, Link } from '../Link/Link';
import { Typography, TypographyProps } from '../Typography/Typography';

type ShowMoreTextProps = {
  /** @default 75 */
  characterLimit?: number;
  text: string;
  typographyProps?: Omit<TypographyProps, 'children' | 'text' | 'sx'>;
  /** When true, `text` is treated as HTML and rendered with markup support */
  isRichText?: boolean;
};

/** Strip tags and decode common entities to get visible text for counting/preview */
const getVisibleText = (html: string): string =>
  html
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&[a-zA-Z#\d]+;/g, '');

export const ShowMoreText = (props: ShowMoreTextProps) => {
  const { characterLimit: propCharLimit, text: propsText, typographyProps, isRichText } = props;

  const characterLimit = propCharLimit && propCharLimit > 0 ? propCharLimit : 75;
  const text = (propsText || '').trim();

  const visibleText = isRichText ? getVisibleText(text) : text;
  const isLong = visibleText.length > characterLimit;

  const [isShowingMore, setIsShowingMore] = useState(false);

  const renderHtml = (content: string) => (
    <InterweaveMarkup
      content={content}
      transform={(node, children) => {
        if (node.tagName === 'A') {
          const href = node.getAttribute('href');
          return href ? <ExternalLink href={href}>{children}</ExternalLink> : undefined;
        }
        return undefined;
      }}
    />
  );

  if (isShowingMore || !isLong) {
    return (
      <Typography {...typographyProps} sx={{ whiteSpace: 'pre-wrap' }}>
        {isRichText ? renderHtml(text) : text}
        {isShowingMore && (
          <>
            {' '}
            <Link onClick={() => setIsShowingMore(false)}>Hide</Link>
          </>
        )}
      </Typography>
    );
  } else {
    return (
      <Typography {...typographyProps}>
        {visibleText.substring(0, characterLimit) + ' ... '}
        <Link onClick={() => setIsShowingMore(true)}>Show More</Link>
      </Typography>
    );
  }
};
